import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Empty, Skeleton } from 'antd';
import dayjs from 'dayjs';
import { useGetDocumentHistoryQuery, type IoDocumentEvent } from '@/api';
import { NAMESPACE } from '@/constants';
import {
  ProgressRow,
  Stage,
  StageDot,
  StageLabel,
  StageTime
} from './DocumentHistory.styles';

interface Props {
  documentId: string;
}

type StageState = 'done' | 'current' | 'pending' | 'failed';

/**
 * The six steps a document moves through: the client's cycle, then Skyrise's.
 *
 * The API does not say which party an event belongs to (IoDocumentEvent is
 * only status/at/eventId), so the split is inferred from order: the
 * countersigner signs after the client, so everything up to and including the
 * first signature is the client's, and everything after it is Skyrise's.
 * See the "signer on each history event" ticket — once the API names the
 * party, this should read `signer.role` instead of counting signatures.
 */
const STAGES = [
  { key: 'sent', i18nKey: 'ioStageSent' },
  { key: 'clientViewed', i18nKey: 'ioStageClientViewed' },
  { key: 'clientSigned', i18nKey: 'ioStageClientSigned' },
  { key: 'skyriseSent', i18nKey: 'ioStageSkyriseSent' },
  { key: 'skyriseViewed', i18nKey: 'ioStageSkyriseViewed' },
  { key: 'skyriseSigned', i18nKey: 'ioStageSkyriseSigned' }
] as const;

type StageKey = (typeof STAGES)[number]['key'];

const isSigned = (status: string): boolean => {
  const value = status.toLowerCase();
  return value.includes('completed') || value.includes('signed');
};

const isViewed = (status: string): boolean => status.toLowerCase().includes('viewed');

const isSent = (status: string): boolean => {
  const value = status.toLowerCase();
  return value.includes('progress') || value.includes('sent');
};

/**
 * First timestamp for each of the six steps, walking events oldest-first and
 * switching to the Skyrise half once the client's signature lands.
 */
const buildReachedAt = (ascending: IoDocumentEvent[]): Partial<Record<StageKey, string>> => {
  const reached: Partial<Record<StageKey, string>> = {};
  let party: 'client' | 'skyrise' = 'client';

  ascending.forEach((event) => {
    const { status, at } = event;

    if (isSent(status)) {
      const key: StageKey = party === 'client' ? 'sent' : 'skyriseSent';
      reached[key] ??= at;
      return;
    }

    if (isViewed(status)) {
      const key: StageKey = party === 'client' ? 'clientViewed' : 'skyriseViewed';
      reached[key] ??= at;
      return;
    }

    if (isSigned(status)) {
      if (party === 'client') {
        reached.clientSigned ??= at;
        /* The countersigner's turn starts here. The provider does not always
           emit a fresh "sent" for it, so the handover doubles as that step. */
        reached.skyriseSent ??= at;
        party = 'skyrise';
      } else {
        reached.skyriseSigned ??= at;
      }
    }
  });

  return reached;
};

/** True for the statuses that stop a document rather than advance it. */
const isTerminalFailure = (status: string): boolean => {
  const value = status.toLowerCase();
  return value.includes('declined') || value.includes('revoked') || value.includes('expired');
};

const labelKey = (status: string): string => {
  const value = status.toLowerCase();
  if (value.includes('completed') || value.includes('signed')) return 'ioStatusCompleted';
  if (value.includes('declined')) return 'ioStatusDeclined';
  if (value.includes('revoked')) return 'ioStatusRevoked';
  if (value.includes('expired')) return 'ioStatusExpired';
  if (value.includes('viewed')) return 'ioStatusViewed';
  if (value.includes('progress') || value.includes('sent')) return 'ioStatusSent';
  return 'ioStatusDraft';
};

/**
 * Where a document has got to, and how it got there.
 *
 * The rail across the top answers "where is this now" at a glance; the full
 * trail underneath carries the detail, collapsed by default because most of
 * the time the rail is the whole answer.
 *
 * The signing provider reports only where a document is now, so the events come
 * from the webhook history stored against the media plan.
 */
export const DocumentHistory: FC<Props> = ({ documentId }) => {
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const { data, isLoading } = useGetDocumentHistoryQuery(documentId);

  const ascending = useMemo(
    () => [...(data ?? [])].sort((a, b) => dayjs(a.at).valueOf() - dayjs(b.at).valueOf()),
    [data]
  );

  /** First time each of the six steps was reached. */
  const reachedAt = useMemo(() => buildReachedAt(ascending), [ascending]);

  const failure = useMemo(
    () => [...ascending].reverse().find((event) => isTerminalFailure(event.status)),
    [ascending]
  );

  if (isLoading) return <Skeleton active paragraph={{ rows: 2 }} />;

  if (!ascending.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('historyEmpty')} />;
  }

  const lastReached = STAGES.filter(({ key }) => reachedAt[key]).pop();

  const stateFor = (key: StageKey): StageState => {
    /* Steps never reached stay pending even when the document failed — the
       failure is its own stage on the rail, so marking the untravelled steps
       red as well would overstate what actually happened. */
    if (!reachedAt[key]) return 'pending';
    return key === lastReached?.key && !failure ? 'current' : 'done';
  };

  return (
    <>
      <ProgressRow>
        {STAGES.map(({ key, i18nKey }) => {
          const state = stateFor(key);
          return (
            <Stage key={key} $state={state}>
              <StageDot $state={state}>{state === 'done' || state === 'current' ? '✓' : ''}</StageDot>
              <StageLabel $muted={state === 'pending'}>{t(i18nKey)}</StageLabel>
              {reachedAt[key] ? (
                <StageTime>{dayjs(reachedAt[key]).format('D MMM, HH:mm')}</StageTime>
              ) : null}
            </Stage>
          );
        })}
        {failure ? (
          <Stage $state="failed">
            <StageDot $state="failed">!</StageDot>
            <StageLabel>{t(labelKey(failure.status))}</StageLabel>
            <StageTime>{dayjs(failure.at).format('D MMM, HH:mm')}</StageTime>
          </Stage>
        ) : null}
      </ProgressRow>

    </>
  );
};
